import React, { FunctionComponent, useRef, useState, useEffect, useCallback } from "react";
import { Button, Classes, Dialog, Intent, FileInput, Callout, ProgressBar, Tabs, Tab } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Flex } from "@rebass/grid";
import apiService, { HTTPError } from "src/services/api";

interface Props {
  isOpen: boolean;
  onClose(): void;
  instanceId?: number;
}

interface SkillFileDetails {
  OriginalFileName: string;
  ContentType: string;
  SizeBytes: number;
  ContentHash: string;
  UploadedByUserID: number;
  inserted_at: string;
  updated_at: string;
}

const SKILL_FILE_ALLOWED_EXTENSIONS = [".md", ".markdown", ".txt"];
const SKILL_FILE_MAX_BYTES = 1 * 1024 * 1024;

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MiB`;
};

const AgenticAIV2ModalDialog: FunctionComponent<Props> = ({ isOpen, onClose, instanceId }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("metadata");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Skill File tab state
  const [skillFileDetails, setSkillFileDetails] = useState<SkillFileDetails | null>(null);
  const [skillFileLoading, setSkillFileLoading] = useState(false);
  const [skillFileBusy, setSkillFileBusy] = useState(false);
  const [skillFileSelected, setSkillFileSelected] = useState<File | null>(null);
  const [skillFileStatus, setSkillFileStatus] = useState<string>("");
  const skillFileInputRef = useRef<HTMLInputElement | null>(null);

  // Agentic Metadata (Excel) tab — last-upload audit state
  const [excelMetadataDetails, setExcelMetadataDetails] = useState<SkillFileDetails | null>(null);
  const [excelMetadataLoading, setExcelMetadataLoading] = useState(false);

  const refreshSkillFileDetails = useCallback(async () => {
    if (!instanceId) return;
    setSkillFileLoading(true);
    try {
      const details = await apiService.get(`/v2/instances/${instanceId}/skill_file/details`);
      setSkillFileDetails(details as SkillFileDetails);
    } catch (e) {
      if (e instanceof HTTPError && e.status === 404) {
        setSkillFileDetails(null);
      } else {
        const errorMessage = (e as any)?.body?.message ?? (e as any)?.message ?? "Failed to load skill file details";
        setSkillFileStatus(`Error: ${errorMessage}`);
        setSkillFileDetails(null);
      }
    } finally {
      setSkillFileLoading(false);
    }
  }, [instanceId]);

  const refreshExcelMetadataDetails = useCallback(async () => {
    if (!instanceId) return;
    setExcelMetadataLoading(true);
    try {
      const details = await apiService.get(`/v2/instances/${instanceId}/agentic_metadata_excel/details`);
      setExcelMetadataDetails(details as SkillFileDetails);
    } catch (e) {
      if (e instanceof HTTPError && e.status === 404) {
        setExcelMetadataDetails(null);
      } else {
        setExcelMetadataDetails(null);
      }
    } finally {
      setExcelMetadataLoading(false);
    }
  }, [instanceId]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab("metadata");
      setSelectedFile(null);
      setUploadStatus("");
      setUploading(false);
      setDownloading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setSkillFileDetails(null);
      setSkillFileSelected(null);
      setSkillFileStatus("");
      setSkillFileBusy(false);
      if (skillFileInputRef.current) {
        skillFileInputRef.current.value = "";
      }
      refreshSkillFileDetails();

      setExcelMetadataDetails(null);
      refreshExcelMetadataDetails();
    }
  }, [isOpen, refreshSkillFileDetails, refreshExcelMetadataDetails]);

  const handleFileChange = (e: React.FormEvent<HTMLInputElement>) => {
    const files = (e.target as HTMLInputElement).files;
    if (files && files.length > 0) {
      const file = files[0];
      const fileName = file.name.toLowerCase();

      if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
        setUploadStatus("Invalid file type. Please select an Excel file (.xlsx or .xls).");
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      setSelectedFile(file);
      setUploadStatus("");
    }
  };

  const handleDownload = async () => {
    if (!instanceId) {
      setUploadStatus("Instance ID is required for download.");
      return;
    }

    setDownloading(true);
    setUploadStatus("");

    try {
      const { blob, filename } = await apiService.getBlob(
        `/v2/instances/${instanceId}/agentic_metadata_excel`
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename ?? `agentic_metadata_v2_${instanceId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setUploadStatus("Download successful!");
    } catch (e: any) {
      let errorMessage = "Please try again.";
      if (e?.body?.message) {
        errorMessage = e.body.message;
      } else if (e?.message) {
        errorMessage = e.message;
      }
      setUploadStatus(`Download failed: ${errorMessage}`);
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !instanceId) {
      setUploadStatus("File and Instance ID are required for upload.");
      return;
    }

    setUploading(true);
    setUploadStatus("");
    setUploadProgress(0.5);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile, selectedFile.name);
      await apiService.postMultipart(
        `/v2/instances/${instanceId}/agentic_metadata_excel`,
        formData
      );

      setUploadProgress(1);
      setUploadStatus("Upload successful!");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      refreshExcelMetadataDetails();
    } catch (e: any) {
      let errorMessage = "Please try again.";
      if (e?.body?.message) {
        errorMessage = e.body.message;
      } else if (e?.message) {
        errorMessage = e.message;
      }
      setUploadStatus(`Upload failed: ${errorMessage}`);
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const handleSkillFileSelect = (e: React.FormEvent<HTMLInputElement>) => {
    const files = (e.target as HTMLInputElement).files;
    if (files && files.length > 0) {
      const file = files[0];
      const lower = file.name.toLowerCase();
      const extOk = SKILL_FILE_ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext));
      if (!extOk) {
        setSkillFileStatus(`Invalid file type. Allowed: ${SKILL_FILE_ALLOWED_EXTENSIONS.join(", ")}`);
        setSkillFileSelected(null);
        if (skillFileInputRef.current) {
          skillFileInputRef.current.value = "";
        }
        return;
      }
      if (file.size > SKILL_FILE_MAX_BYTES) {
        setSkillFileStatus(`File exceeds ${formatBytes(SKILL_FILE_MAX_BYTES)} limit (${formatBytes(file.size)}).`);
        setSkillFileSelected(null);
        if (skillFileInputRef.current) {
          skillFileInputRef.current.value = "";
        }
        return;
      }
      setSkillFileSelected(file);
      setSkillFileStatus("");
    }
  };

  const handleSkillFileUpload = async () => {
    if (!skillFileSelected || !instanceId) return;
    setSkillFileBusy(true);
    setSkillFileStatus("");
    try {
      const formData = new FormData();
      // Force text/markdown for .md/.markdown extensions; .txt files are
      // sent as text/plain. The server enforces both via its whitelist.
      const lower = skillFileSelected.name.toLowerCase();
      const contentType = lower.endsWith(".txt") ? "text/plain" : "text/markdown";
      const blob = new Blob([skillFileSelected], { type: contentType });
      formData.append("file", blob, skillFileSelected.name);
      const details = await apiService.postMultipart<SkillFileDetails>(
        `/v2/instances/${instanceId}/skill_file`,
        formData
      );
      setSkillFileDetails(details);
      setSkillFileSelected(null);
      if (skillFileInputRef.current) {
        skillFileInputRef.current.value = "";
      }
      setSkillFileStatus("Upload successful!");
    } catch (e: any) {
      const errorMessage = e?.body?.message ?? e?.message ?? "Upload failed";
      setSkillFileStatus(`Upload failed: ${errorMessage}`);
    } finally {
      setSkillFileBusy(false);
    }
  };

  const handleSkillFileDownload = async () => {
    if (!instanceId || !skillFileDetails) return;
    setSkillFileBusy(true);
    setSkillFileStatus("");
    try {
      const response = await apiService.get(`/v2/instances/${instanceId}/skill_file`);
      if (!response?.url) {
        throw new Error("No download URL received from server");
      }
      const fileResponse = await fetch(response.url);
      if (!fileResponse.ok) {
        throw new Error("Failed to download file from blob storage");
      }
      const blob = await fileResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = response.OriginalFileName ?? `skill_file_${instanceId}.md`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSkillFileStatus("Download successful!");
    } catch (e: any) {
      const errorMessage = e?.body?.message ?? e?.message ?? "Download failed";
      setSkillFileStatus(`Download failed: ${errorMessage}`);
    } finally {
      setSkillFileBusy(false);
    }
  };

  const handleSkillFileDelete = async () => {
    if (!instanceId || !skillFileDetails) return;
    if (!window.confirm(`Delete skill file "${skillFileDetails.OriginalFileName}"? This cannot be undone.`)) {
      return;
    }
    setSkillFileBusy(true);
    setSkillFileStatus("");
    try {
      await apiService.deleteNoContent(`/v2/instances/${instanceId}/skill_file`);
      setSkillFileDetails(null);
      setSkillFileStatus("Skill file deleted.");
    } catch (e: any) {
      const errorMessage = e?.body?.message ?? e?.message ?? "Delete failed";
      setSkillFileStatus(`Delete failed: ${errorMessage}`);
    } finally {
      setSkillFileBusy(false);
    }
  };

  return (
    <Dialog
      title="Metadata Management (v2)"
      isOpen={isOpen}
      onClose={onClose}
      icon={IconNames.EXCHANGE}
    >
      <div className={Classes.DIALOG_BODY}>
        <style>
          {`
            .bp4-tab:focus {
              outline: none !important;
            }
            .bp4-tab[aria-selected="true"] {
              box-shadow: none !important;
            }
            .bp4-callout.bp4-callout-icon > .bp4-icon:first-child {
              top: 18px !important;
            }
          `}
        </style>
        <Tabs
          id="metadata-v2-tabs"
          selectedTabId={activeTab}
          onChange={(newTabId) => {
            setActiveTab(newTabId as string);
            setUploadStatus("");
          }}
        >
          <Tab
            id="metadata"
            title="Agentic Metadata"
            panel={
              <div style={{ paddingTop: 16 }}>
                <Callout intent={Intent.PRIMARY} icon={IconNames.INFO_SIGN} style={{ marginBottom: 16, display: "flex", alignItems: "flex-start" }}>
                  <div>
                    Download the v2 metadata Excel file (three sheets: Dimensions, Inputs, Outputs), edit the agentic context, then upload the edited file to apply changes.
                    <br />
                    <strong>Only Excel files (.xlsx, .xls) are supported. Uploads require the instance to be in Synced or Published state.</strong>
                  </div>
                </Callout>

                {excelMetadataLoading ? (
                  <Callout intent={Intent.NONE} style={{ marginBottom: 16 }}>Loading metadata upload details...</Callout>
                ) : excelMetadataDetails ? (
                  <Callout intent={Intent.SUCCESS} icon={IconNames.DOCUMENT} style={{ marginBottom: 16 }}>
                    <div>
                      <strong>{excelMetadataDetails.OriginalFileName}</strong>
                      <br />
                      {formatBytes(excelMetadataDetails.SizeBytes)} &middot; uploaded by user {excelMetadataDetails.UploadedByUserID}
                      <br />
                      Last updated: {new Date(excelMetadataDetails.updated_at).toLocaleString()}
                    </div>
                  </Callout>
                ) : (
                  <Callout intent={Intent.NONE} style={{ marginBottom: 16 }}>
                    No metadata upload recorded for this instance yet.
                  </Callout>
                )}

                <Flex mb={3} flexDirection="row" alignItems="stretch" style={{ width: 360 }}>
                  <FileInput
                    text={selectedFile ? selectedFile.name : "Choose Excel file..."}
                    inputProps={{ accept: ".xlsx,.xls", ref: fileInputRef, onChange: handleFileChange }}
                    disabled={uploading || downloading}
                    style={{ marginRight: 8, width: 220 }}
                  />
                  <Button
                    icon={IconNames.UPLOAD}
                    text="Upload"
                    onClick={async () => {
                      await handleUpload();
                    }}
                    disabled={!selectedFile || uploading || downloading}
                    intent={Intent.PRIMARY}
                    loading={uploading}
                  />
                </Flex>

                <Flex flexDirection="row" mb={3}>
                  <Button
                    icon={IconNames.DOWNLOAD}
                    text="Download"
                    onClick={handleDownload}
                    disabled={!instanceId || downloading || uploading}
                    loading={downloading}
                  />
                </Flex>

                {uploading && (
                  <div style={{ width: 360, marginBottom: 8 }}>
                    <ProgressBar value={uploadProgress} animate intent={Intent.PRIMARY} />
                  </div>
                )}
                {uploadStatus && activeTab === "metadata" && (
                  <Callout intent={uploadStatus.includes("success") ? Intent.SUCCESS : Intent.DANGER}>
                    {uploadStatus}
                  </Callout>
                )}
              </div>
            }
          />
          <Tab
            id="skill_file"
            title="Skill File"
            panel={
              <div style={{ paddingTop: 16 }}>
                <Callout intent={Intent.PRIMARY} icon={IconNames.INFO_SIGN} style={{ marginBottom: 16, display: "flex", alignItems: "flex-start" }}>
                  <div>
                    Upload a Markdown file informing agent behavior on this model instance. Typical sections: Model Flow, Key and Complex Inputs, Key Outputs.
                    <br />
                    <strong>Allowed: {SKILL_FILE_ALLOWED_EXTENSIONS.join(", ")}. Max {formatBytes(SKILL_FILE_MAX_BYTES)}. Re-upload replaces the existing file.</strong>
                  </div>
                </Callout>

                {skillFileLoading ? (
                  <Callout intent={Intent.NONE}>Loading skill file details...</Callout>
                ) : skillFileDetails ? (
                  <Callout intent={Intent.SUCCESS} icon={IconNames.DOCUMENT} style={{ marginBottom: 16 }}>
                    <div>
                      <strong>{skillFileDetails.OriginalFileName}</strong>
                      <br />
                      {formatBytes(skillFileDetails.SizeBytes)} &middot; uploaded by user {skillFileDetails.UploadedByUserID}
                      <br />
                      Last updated: {new Date(skillFileDetails.updated_at).toLocaleString()}
                    </div>
                  </Callout>
                ) : (
                  <Callout intent={Intent.NONE} style={{ marginBottom: 16 }}>
                    No skill file uploaded for this instance yet.
                  </Callout>
                )}

                <Flex mb={3} flexDirection="row" alignItems="stretch" style={{ width: 360 }}>
                  <FileInput
                    text={skillFileSelected ? skillFileSelected.name : "Choose Markdown file..."}
                    inputProps={{
                      accept: SKILL_FILE_ALLOWED_EXTENSIONS.join(","),
                      ref: skillFileInputRef,
                      onChange: handleSkillFileSelect,
                    }}
                    disabled={skillFileBusy}
                    style={{ marginRight: 8, width: 220 }}
                  />
                  <Button
                    icon={IconNames.UPLOAD}
                    text={skillFileDetails ? "Replace" : "Upload"}
                    onClick={handleSkillFileUpload}
                    disabled={!skillFileSelected || skillFileBusy}
                    intent={Intent.PRIMARY}
                    loading={skillFileBusy && !!skillFileSelected}
                  />
                </Flex>

                <Flex flexDirection="row" mb={3}>
                  <Button
                    icon={IconNames.DOWNLOAD}
                    text="Download"
                    onClick={handleSkillFileDownload}
                    disabled={!skillFileDetails || skillFileBusy}
                    style={{ marginRight: 8 }}
                  />
                  <Button
                    icon={IconNames.TRASH}
                    text="Delete"
                    onClick={handleSkillFileDelete}
                    disabled={!skillFileDetails || skillFileBusy}
                    intent={Intent.DANGER}
                  />
                </Flex>

                {skillFileStatus && activeTab === "skill_file" && (
                  <Callout intent={skillFileStatus.toLowerCase().includes("success") || skillFileStatus.toLowerCase().includes("deleted") ? Intent.SUCCESS : Intent.DANGER}>
                    {skillFileStatus}
                  </Callout>
                )}
              </div>
            }
          />
        </Tabs>
      </div>
    </Dialog>
  );
};

export default AgenticAIV2ModalDialog;
